import React, { Component } from 'react';
import _PublicationService from 'modules/PublicationService';
import _notification from 'modules/notification';
import { Card, Table, Modal, Tag, DatePicker, InputNumber } from 'antd';
import EmptyState from 'components/enterprise/EmptyState';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import PageHeader from 'components/enterprise/PageHeader';
import DropdownList from 'components/controls/DropdownList';
import { routesAdmin } from 'routes/Routes';
import ConfirmPopup from '../popup/ConfirmPopup';
import MyInput from 'components/controls/MyInput';
import TextArea from 'components/controls/TextArea';
import ButtonComponent from 'components/controls/Button';
import FileUpload from 'components/controls/FileUpload';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';
import moment from 'moment';

// Phase C.1 (Enterprise Publication Foundation): admin management for candidate-facing publications
// (Recruitment Advertisement, Candidate User Guide, Important Instructions, Eligible Candidates —
// Examination/Interview, Selected & Waiting List, Other/Notice), replacing the previous developer-
// dependent model of hand-placing PDFs in IIS/application folders and hard-binding paths in frontend
// code. Follows the existing admin list+form screen convention (CategoryMaster_new.jsx et al.) — no new
// UI pattern is introduced.
const PUBLICATION_TYPES = [
    { Id: 'RecruitmentAdvertisement', Name: 'Recruitment Advertisement' },
    { Id: 'CandidateUserGuide', Name: 'Candidate User Guide' },
    { Id: 'ImportantInstructions', Name: 'Important Instructions' },
    { Id: 'EligibleCandidatesExamination', Name: 'Eligible Candidates - Examination' },
    { Id: 'EligibleCandidatesInterview', Name: 'Eligible Candidates - Interview' },
    { Id: 'SelectedAndWaitingList', Name: 'Selected & Waiting List' },
    { Id: 'OtherNotice', Name: 'Other / Notice' }
];

const TYPE_LABELS = PUBLICATION_TYPES.reduce((map, t) => { map[t.Id] = t.Name; return map; }, {});

// Phase C.1.1 fix: antd v3's <Tag> only renders readable white-on-color text for its actual PRESET
// color names (pink/red/orange/green/cyan/blue/purple/etc.) — "default" is NOT one of those presets.
// Passing color="default" made antd treat it as an arbitrary custom color, which set text to white
// (assuming a colored background) while "default" is not a valid CSS color so the background stayed
// near-white — white text on a near-white background, i.e. an unreadable Draft status cell. Omitting
// the color entirely (undefined) for Draft renders antd's own neutral/default Tag styling instead.
const STATUS_TAG_COLOR = { Draft: undefined, Published: 'green', Archived: 'orange' };

class PublicationMaster_new extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pagination: { pageSize: 10, current: 1 },
            data: [],
            loading: false,
            showCreateModal: false,
            supersedeTargetId: null, // non-null => the create form is acting as a Supersede for this Id
            rowData: {},
            selectedFile: null,
            fileError: '',
            formError: '',
            showArchiveConfirm: false,
            archiveTargetId: null,
        };
    }

    componentDidMount() {
        this.getAll();
    }

    getAll = async () => {
        this.setState({ loading: true });
        try {
            const response = await _PublicationService.getAll();
            this.setState({ data: response.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    // Pagination bug fix (same class as InterviewScheduleMaster_new.jsx): the Table's pagination prop
    // is bound to state, but without this onChange wired, clicking a page number never actually
    // updated `pagination.current` - the page selector changed visually but the displayed rows never did.
    onTableAction = (pagination) => {
        this.setState({ pagination });
    }

    onTextChange = ({ target: { name, value } }) => {
        this.setState({ rowData: { ...this.state.rowData, [name]: value } });
    }

    onTypeChange = (event) => {
        this.setState({ rowData: { ...this.state.rowData, PublicationType: event.target.value } });
    }

    onDisplayOrderChange = (value) => {
        this.setState({ rowData: { ...this.state.rowData, DisplayOrder: value } });
    }

    onPublicationDateChange = (date) => {
        this.setState({ rowData: { ...this.state.rowData, PublicationDate: date ? date.format("YYYY-MM-DDT00:00:00") : "" } });
    }

    onEffectiveDateChange = (date) => {
        this.setState({ rowData: { ...this.state.rowData, EffectiveDate: date ? date.format("YYYY-MM-DDT00:00:00") : "" } });
    }

    onFileDrop = (file) => {
        this.setState({ selectedFile: file, fileError: '' });
    }

    onFileError = (message) => {
        this.setState({ fileError: message, selectedFile: null });
    }

    openCreateModal = () => {
        this.setState({ showCreateModal: true, supersedeTargetId: null, rowData: { DisplayOrder: 0 }, selectedFile: null, fileError: '', formError: '' });
    }

    openSupersedeModal = (record) => {
        this.setState({
            showCreateModal: true,
            supersedeTargetId: record.Id,
            rowData: {
                PublicationType: record.PublicationType,
                Title: record.Title,
                Description: record.Description,
                PublicationLanguage: record.PublicationLanguage,
                DisplayOrder: record.DisplayOrder
            },
            selectedFile: null,
            fileError: '',
            formError: ''
        });
    }

    closeCreateModal = () => {
        this.setState({ showCreateModal: false, supersedeTargetId: null, rowData: {}, selectedFile: null, fileError: '', formError: '' });
    }

    validate = () => {
        const { rowData, selectedFile } = this.state;
        if (!rowData.PublicationType) { return 'Please select a Publication Type.'; }
        if (!rowData.Title || !rowData.Title.trim()) { return 'Please enter a Title.'; }
        if (!rowData.PublicationDate) { return 'Please select a Publication Date.'; }
        if (!selectedFile) { return 'Please attach a PDF document.'; }
        return '';
    }

    handleSave = async () => {
        const error = this.validate();
        if (error) {
            this.setState({ formError: error });
            return;
        }

        this.setState({ loading: true });
        try {
            const response = this.state.supersedeTargetId
                ? await _PublicationService.supersede(this.state.supersedeTargetId, this.state.rowData, this.state.selectedFile)
                : await _PublicationService.create(this.state.rowData, this.state.selectedFile);

            if (response.status === 200) {
                _notification.showMessage('success', this.state.supersedeTargetId
                    ? 'Replacement publication created and published; the previous version has been archived.'
                    : 'Publication saved as Draft.');
                this.closeCreateModal();
                this.getAll();
            } else {
                this.setState({ loading: false });
                _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            // TODO
            this.setState({ loading: false });
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handlePublish = async (record) => {
        try {
            const response = await _PublicationService.publish(record.Id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Publication published.');
                this.getAll();
            } else {
                _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleUnpublish = async (record) => {
        try {
            const response = await _PublicationService.unpublish(record.Id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Publication unpublished (hidden from candidates).');
                this.getAll();
            } else {
                _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onArchiveClick = (record) => {
        this.setState({ showArchiveConfirm: true, archiveTargetId: record.Id });
    }

    onConfirmArchive = async () => {
        const id = this.state.archiveTargetId;
        this.setState({ showArchiveConfirm: false, archiveTargetId: null });
        try {
            const response = await _PublicationService.archive(id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Publication archived.');
                this.getAll();
            } else {
                _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleViewDocument = async (record) => {
        try {
            const response = await _PublicationService.getDocumentAccessUrl(record.Id);
            if (response.status === 200 && response.data && response.data.Url) {
                window.open(response.data.Url, '_blank');
            } else {
                _notification.showMessage('error', 'Document could not be opened.');
            }
        } catch (e) {
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    render() {
        const { rowData, fileError, formError, supersedeTargetId } = this.state;

        const TableColumns = [
            { title: 'Title', dataIndex: 'Title', key: 'Title', width: '28%' },
            {
                title: 'Type', dataIndex: 'PublicationType', key: 'PublicationType', width: '18%',
                render: (value) => TYPE_LABELS[value] || value
            },
            {
                title: 'Status', dataIndex: 'Status', key: 'Status', width: '10%',
                render: (value, record) => (
                    <>
                        <Tag color={STATUS_TAG_COLOR[value]}>{value}</Tag>
                        {value === 'Published' && !record.IsActive && <Tag color="red">Hidden</Tag>}
                    </>
                )
            },
            {
                title: 'Publication Date', dataIndex: 'PublicationDate', key: 'PublicationDate', width: '13%',
                render: (value) => value ? moment(value).format('DD-MMM-YYYY') : ''
            },
            { title: 'Language', dataIndex: 'PublicationLanguage', key: 'PublicationLanguage', width: '8%' },
            { title: 'Order', dataIndex: 'DisplayOrder', key: 'DisplayOrder', width: '6%' },
            {
                title: 'Action', dataIndex: '', key: 'Id', width: '17%',
                render: (record) => (
                    <>
                        <a className="d-block mb-1" onClick={() => this.handleViewDocument(record)}>View PDF</a>
                        {record.Status === 'Draft' && (
                            <ButtonComponent type="button" value="Publish" className="button-global button-primary small autoHeight mr-2 mb-1" onClick={() => this.handlePublish(record)} />
                        )}
                        {record.Status === 'Published' && record.IsActive && (
                            <ButtonComponent type="button" value="Unpublish" className="button-global button-border small autoHeight mr-2 mb-1" onClick={() => this.handleUnpublish(record)} />
                        )}
                        {record.Status === 'Published' && !record.IsActive && (
                            <ButtonComponent type="button" value="Republish" className="button-global button-primary small autoHeight mr-2 mb-1" onClick={() => this.handlePublish(record)} />
                        )}
                        {record.Status === 'Published' && (
                            <ButtonComponent type="button" value="Replace" className="button-global button-border small autoHeight mr-2 mb-1" onClick={() => this.openSupersedeModal(record)} />
                        )}
                        {record.Status !== 'Archived' && (
                            <ButtonComponent type="button" value="Archive" className="button-global button-danger small autoHeight mb-1" onClick={() => this.onArchiveClick(record)} />
                        )}
                    </>
                )
            },
        ];

        if (this.state.loading && this.state.data.length === 0)
            return <LoadingSpinner />

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Publications"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Publications' }]}
                />
                <div className="analysisWrap">
                    <div style={{ textAlign: 'right' }}>
                        <div className="max1200 m-auto mb-2">
                            <div className="d-flex align-items-center justify-content-end">
                                <a className="button-global button-border addbutton autoWidth d-inline-block text-center autoHeight mb-3 d-flex align-items-center justify-content-center"
                                    onClick={this.openCreateModal}>+ Add Publication</a>
                            </div>
                        </div>
                    </div>

                    <div className="max1200 m-auto">
                        <Table size='small'
                            bordered
                            rowKey='Id'
                            columns={TableColumns}
                            dataSource={this.state.data}
                            pagination={this.state.pagination}
                            onChange={this.onTableAction}
                            footer={() => <label style={{ textAlign: 'right', marginRight: '20px' }}>Total Records: {this.state.data.length}</label>}
                            className="table-striped-rows admin-table"
                            locale={{ emptyText: <EmptyState icon="inbox" title="No Publications Found" description="No publications have been created yet." /> }}
                        />
                    </div>
                </div>

                <Modal
                    title={supersedeTargetId ? "Replace Publication (Supersede + Archive)" : "Add Publication"}
                    visible={this.state.showCreateModal}
                    onCancel={this.closeCreateModal}
                    footer={null}
                    width={600}
                    className="modelPopAnt admin-home"
                    destroyOnClose={true}
                >
                    <Card>
                        {supersedeTargetId && (
                            <p className="mb-3">
                                This creates a new publication record linked to the one it replaces. The
                                previous version will be archived automatically once this one is published.
                            </p>
                        )}
                        <div className="mb-2">
                            <DropdownList
                                name="PublicationType"
                                value={rowData.PublicationType || ''}
                                onChange={this.onTypeChange}
                                placeholder="Select Publication Type"
                                heading="Publication Type"
                                isRequired={true}
                                options={PUBLICATION_TYPES}
                            />
                        </div>
                        <div className="mb-2">
                            <MyInput type="text" name="Title" heading="Title" isRequired={true}
                                value={rowData.Title || ''} onChange={this.onTextChange} />
                        </div>
                        <div className="mb-2">
                            <TextArea name="Description" heading="Description (optional)" value={rowData.Description || ''} onChange={this.onTextChange} />
                        </div>
                        <div className="row">
                            <div className="col-sm-6 mb-2">
                                <MyInput type="text" name="PublicationLanguage" heading="Language (e.g. en, mr)"
                                    value={rowData.PublicationLanguage || ''} onChange={this.onTextChange} />
                            </div>
                            <div className="col-sm-6 mb-2">
                                <label className="card-holder-name">Display Order</label>
                                <InputNumber min={0} value={rowData.DisplayOrder || 0} onChange={this.onDisplayOrderChange} style={{ width: '100%' }} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-6 mb-2">
                                <label className="card-holder-name">Publication Date<span className="color-red">*</span></label>
                                <DatePicker onChange={this.onPublicationDateChange} format="DD-MMMM-YYYY" style={{ width: '100%' }}
                                    defaultValue={rowData.PublicationDate && moment(rowData.PublicationDate).isValid() ? moment(rowData.PublicationDate) : undefined} />
                            </div>
                            <div className="col-sm-6 mb-2">
                                <label className="card-holder-name">Effective Date (optional)</label>
                                <DatePicker onChange={this.onEffectiveDateChange} format="DD-MMMM-YYYY" style={{ width: '100%' }}
                                    defaultValue={rowData.EffectiveDate && moment(rowData.EffectiveDate).isValid() ? moment(rowData.EffectiveDate) : undefined} />
                            </div>
                        </div>
                        <div className="mb-2">
                            <label className="card-holder-name">PDF Document (max 10 MB)<span className="color-red">*</span></label>
                            <FileUpload filetype="pdf" onFileDrop={this.onFileDrop} onError={this.onFileError} />
                            {fileError && <div className="color-red mt-1">{fileError}</div>}
                        </div>
                        {formError && <div className="color-red mb-2">{formError}</div>}
                        <div className="pt-2 buttonAlignCenter">
                            <ButtonComponent type="button" value="Cancel" onClick={this.closeCreateModal} className="button-global button-secondary mr-4 autoHeight small" />
                            <ButtonComponent type="button" value={supersedeTargetId ? "Save & Publish Replacement" : "Save as Draft"} onClick={this.handleSave} className="button-global button-primary autoHeight small" disabled={this.state.loading} />
                        </div>
                    </Card>
                </Modal>

                <Modal
                    centered={true}
                    width={400}
                    className="modelPopAnt admin-home"
                    visible={this.state.showArchiveConfirm}
                    onCancel={() => this.setState({ showArchiveConfirm: false, archiveTargetId: null })}
                    footer={false}
                >
                    <ConfirmPopup
                        onCancel={() => this.setState({ showArchiveConfirm: false, archiveTargetId: null })}
                        onOk={this.onConfirmArchive}
                        message="Are you sure you want to archive this publication? It will no longer be visible to candidates. The record is retained for audit, not deleted."
                    />
                </Modal>
            </div>
        )
    };
}

export default PublicationMaster_new;
